import axiosConfig from './axiosConfig';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createListFetcher } from './listFetchers';
import { queryClient } from './queryClient';

/**
 * Get list of all staff users
 * @returns {Promise} Response with list of staff users
 */
export const getStaffList = createListFetcher('staff');

export const useStaffList = (queryParams = {}, options = {}) => useQuery({
    queryKey: ['staffs', queryParams],
    queryFn: async () => {
        const response = await getStaffList(queryParams);
        return response.data?.data ?? [];
    },
    keepPreviousData: true,
    retry: 1,
    ...options,
});

export const useCreateStaff = (options = {}) => {
    return useMutation({
        mutationFn: createStaff,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['staffs'] }),
        ...options,
    });
};

export const useUpdateStaff = (options = {}) => {
    return useMutation({
        mutationFn: ({ id, staffData }) => updateStaff(id, staffData),
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: ['staffs'] });
            if (variables?.id) {
                queryClient.invalidateQueries({ queryKey: ['staff', variables.id] });
            }
        },
        ...options,
    });
};

/**
 * Create a new staff user
 * @param {Object} staffData Staff user data to create
 * @returns {Promise} Response with created staff user data
 */
export const createStaff = async (staffData) => {
    try {
        const response = await axiosConfig.post('staff', staffData);
        return response;
    } catch (error) {
        throw error || 'Failed to create staff user';
    }
};

/**
 * Get staff user by ID
 * @param {string} staffId Staff user ID to retrieve
 * @returns {Promise} Response with staff user data
 */
export const getStaffById = async (staffId) => {
    try {
        const response = await axiosConfig.get(`staff/${staffId}`);
        return response;
    } catch (error) {
        throw error || 'Failed to get staff user';
    }
};

/**
 * Update an existing staff user
 * @param {string} staffId Staff user ID to update
 * @param {Object} staffData Updated staff user data
 * @returns {Promise} Response with updated staff user data
 */
export const updateStaff = async (staffId, staffData) => {
    try {
        const response = await axiosConfig.put(`staff/${staffId}`, staffData);
        return response;
    } catch (error) {
        throw error || 'Failed to update staff user';
    }
};

/**
 * Delete a staff user
 * @param {string} staffId Staff user ID to delete
 * @returns {Promise} Response from delete operation
 */
export const deleteStaff = async (staffId) => {
    try {
        const response = await axiosConfig.delete(`staff/${staffId}`);
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Reset password (self-service OTP)
 * @param {string} staffId Staff user ID
 * @param {Object} otpData OTP data
 * @returns {Promise} Response from reset password operation
 */
export const resetPassword = async (staffId, otpData) => {
    try {
        const response = await axiosConfig.put(`staff/resetpassword/${staffId}`, otpData);
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Admin reset password for a staff user
 * @param {string} staffId Staff user ID
 * @param {Object} passwordData New password data
 * @returns {Promise} Response from admin reset password operation
 */
export const adminResetPassword = async (staffId, passwordData) => {
    try {
        const response = await axiosConfig.put(`admin/resetpassword/${staffId}`, passwordData);
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * List all users (admin only)
 * @param {Object} queryParams Query parameters
 * @returns {Promise} Response with list of all users
 */
export const getAllUsersAdmin = async (queryParams = {}) => {
    try {
        const response = await axiosConfig.get('admin/users', { params: queryParams });
        return response;
    } catch (error) {
        throw error;
    }
};

/**
 * Get list of all roles
 * @param {Object} queryParams Query parameters
 * @returns {Promise} Response with list of roles
 */
export const getRoles = async (queryParams = {}) => {
    try {
        const response = await axiosConfig.get('roles', { params: queryParams });
        return response;
    } catch (error) {
        throw error;
    }
};
